import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, QrCode, RefreshCcw, Trash2, Download, Layers, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { v: "umbrella", label: "Ombrellone", defaultPrefix: "Ombrellone" },
  { v: "table", label: "Tavolo", defaultPrefix: "Tavolo" },
  { v: "sunbed", label: "Lettino", defaultPrefix: "Lettino" },
  { v: "vip", label: "Gazebo VIP", defaultPrefix: "Gazebo" },
  { v: "other", label: "Altro", defaultPrefix: "Postazione" },
];

export default function AdminStations() {
  const [stations, setStations] = useState([]);
  const [type, setType] = useState("all");
  const [qrFor, setQrFor] = useState(null);

  const load = async () => setStations((await api.get("/admin/stations")).data);
  useEffect(() => { load(); }, []);

  const filtered = type === "all" ? stations : stations.filter((s) => s.type === type);

  const regen = async (s) => {
    if (!window.confirm(`Rigenerare il QR di ${s.name}?`)) return;
    await api.post(`/admin/stations/${s.id}/regenerate-token`); load();
    toast.success("Nuovo token generato");
  };
  const toggle = async (s) => {
    await api.patch(`/admin/stations/${s.id}`, { active: !s.active }); load();
  };
  const del = async (s) => {
    if (!window.confirm(`Eliminare ${s.name}?`)) return;
    await api.delete(`/admin/stations/${s.id}`); load();
  };

  const downloadPdf = async (kind = "sheet") => {
    try {
      const path = kind === "labels" ? "qr-labels.pdf" : "qr-sheet.pdf";
      const url = `${BACKEND}/api/admin/stations/${path}?type=${type}&only_active=false`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        const t = await resp.text();
        toast.error(`PDF non generato: ${resp.status} ${t}`);
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "labels" ? `sarabi-etichette-${type}.pdf` : `sarabi-qr-${type}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="eyebrow">Gestione</div>
      <h1 className="text-4xl mt-1">Postazioni & QR</h1>

      <div className="flex justify-between items-center mt-6 gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {[["all", "Tutte"], ["umbrella", "Ombrelloni"], ["table", "Tavoli"], ["sunbed", "Lettini"], ["vip", "VIP"], ["other", "Altre"]].map(([k, l]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs border ${type === k ? "bg-bronze-600 text-white border-bronze-600" : "bg-white border-sand-300"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={() => downloadPdf("sheet")} data-testid="qr-pdf-btn">
            <FileText className="w-4 h-4 mr-1" />Esporta A4
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => downloadPdf("labels")} data-testid="qr-labels-btn">
            <FileText className="w-4 h-4 mr-1" />Esporta Etichetta 10x15
          </Button>
          <BulkStationDialog onSaved={load} />
          <NewStationDialog onSaved={load} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filtered.map((s) => (
          <div key={s.id} data-testid={`station-${s.id}`} className={`card-elevated p-3 ${!s.active ? "opacity-50" : ""}`}>
            <div className="flex justify-between">
              <div className="font-serif text-xl">{s.name}</div>
              <Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`toggle-${s.id}`} />
            </div>
            <div className="text-xs text-ink-500 mt-1">{s.type}</div>
            <div className="mt-2 flex gap-1 flex-wrap">
              <Button size="sm" variant="outline" className="rounded-full text-xs px-2 h-7" onClick={() => setQrFor(s)} data-testid={`qr-${s.id}`}><QrCode className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs px-2 h-7" onClick={() => regen(s)} data-testid={`regen-${s.id}`}><RefreshCcw className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs px-2 h-7 text-red-700" onClick={() => del(s)} data-testid={`del-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-8 text-ink-500">Nessuna postazione</div>}
      </div>

      <Dialog open={!!qrFor} onOpenChange={(o) => !o && setQrFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>QR — {qrFor?.name}</DialogTitle></DialogHeader>
          {qrFor && (
            <div className="flex flex-col items-center gap-3">
              <img src={`${BACKEND}/api/admin/stations/${qrFor.id}/qr.svg`} className="w-56 h-56 border border-sand-300 rounded-2xl bg-white" alt="QR" />
              <div className="text-xs break-all text-ink-500">/order/{qrFor.token}</div>
              <a className="btn-primary text-xs" href={`${BACKEND}/api/admin/stations/${qrFor.id}/qr.svg`} download={`sarabi-${qrFor.name}.svg`}>
                <Download className="w-4 h-4 mr-1" /> Scarica SVG
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewStationDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "umbrella", name: "", description: "", active: true });
  const save = async () => {
    try { await api.post("/admin/stations", f); setOpen(false); onSaved(); setF({ type: "umbrella", name: "", description: "", active: true }); toast.success("Postazione creata"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="new-station-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuova</Button></DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle>Nuova postazione</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Tipo</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="new-station-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nome / Numero</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-station-name" /></div>
          <Button onClick={save} className="w-full" data-testid="new-station-save">Crea</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkStationDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "umbrella", name_prefix: "Ombrellone", start: 1, end: 20, active: true });

  const setType = (v) => {
    const def = TYPE_OPTIONS.find((o) => o.v === v)?.defaultPrefix || "";
    setF((prev) => ({ ...prev, type: v, name_prefix: def }));
  };

  const save = async () => {
    if (!f.name_prefix.trim()) return toast.error("Prefisso obbligatorio");
    if (f.start > f.end) return toast.error("Il numero iniziale deve essere ≤ finale");
    if (f.end - f.start + 1 > 500) return toast.error("Massimo 500 postazioni per volta");
    try {
      const r = await api.post("/admin/stations/bulk", f);
      setOpen(false); onSaved();
      const created = r.data.created?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      toast.success(`Create ${created} postazioni${skipped ? ` — ${skipped} saltate (già esistenti)` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const count = Math.max(0, f.end - f.start + 1);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="bulk-station-btn">
          <Layers className="w-4 h-4 mr-1" />Creazione massiva
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Creazione massiva postazioni</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Tipo</Label>
            <Select value={f.type} onValueChange={setType}>
              <SelectTrigger data-testid="bulk-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Prefisso nome</Label>
            <Input value={f.name_prefix} onChange={(e) => setF({ ...f, name_prefix: e.target.value })} data-testid="bulk-prefix" placeholder="Es. Ombrellone" />
            <p className="text-[11px] text-ink-500 mt-1">Ogni postazione avrà nome “{f.name_prefix} 1”, “{f.name_prefix} 2”, ecc.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Da</Label>
              <Input type="number" min="1" value={f.start} onChange={(e) => setF({ ...f, start: parseInt(e.target.value) || 1 })} data-testid="bulk-start" />
            </div>
            <div><Label>A</Label>
              <Input type="number" min="1" value={f.end} onChange={(e) => setF({ ...f, end: parseInt(e.target.value) || 1 })} data-testid="bulk-end" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="bulk-active" />
            Attive immediatamente
          </label>
          <div className="text-sm bg-sand-50 border border-sand-300 rounded-xl p-3">
            Verranno create <b>{count}</b> postazioni: <span className="text-bronze-700">{f.name_prefix} {f.start}</span> … <span className="text-bronze-700">{f.name_prefix} {f.end}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} data-testid="bulk-save">Crea {count} postazioni</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
